import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';

import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { NgRequest } from '../shared/ng-request';
import { NgResponse } from '../shared/ng-response';
import { HttpErrorHandler, HandleError, HandleBlobError } from './http-error-handler';

export abstract class HttpClientService {
    protected readonly handleError: HandleError;
    protected readonly handleBlobError: HandleBlobError;
    private readonly apiUrl: string = environment.apiURL;
    private readonly jsonOptions = environment.jsonOptions;
    private readonly withCredentials: boolean = environment.withCredentials;

  protected constructor(
    readonly service: string,
    readonly httpClient: HttpClient,
    readonly httpErrorHandler: HttpErrorHandler) {
    this.handleError = httpErrorHandler.createErrorHandler(service);
    this.handleBlobError = httpErrorHandler.createBlobErrorHandler(service);
  }

  protected delete<TResponse>(operation: string, urlPath: string): Observable<NgResponse<TResponse>> {
      return this.httpClient.delete<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected get<TResponse>(operation: string, urlPath: string): Observable<NgResponse<TResponse>> {
      return this.httpClient.get<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

    protected getBlobResponse(operation: string, urlPath: string): Observable<HttpResponse<Blob>> {
        return this.httpClient.get(`${this.apiUrl}${urlPath}`, { headers: this.jsonOptions.headers, observe: 'response', responseType: 'blob', withCredentials: this.withCredentials })
      .pipe(catchError(this.handleBlobError(operation)));
  }

  protected post<TResponse>(operation: string, urlPath: string): Observable<NgResponse<TResponse>> {
      return this.httpClient.post<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, null, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected postBody<TRequest, TResponse>(operation: string, urlPath: string, request: NgRequest<TRequest>): Observable<NgResponse<TResponse>> {
      return this.httpClient.post<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, request, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected postBodyWithBlobResponse<TRequest>(operation: string, urlPath: string, request: NgRequest<TRequest>): Observable<HttpResponse<Blob>> {
      return this.httpClient.post(`${this.apiUrl}${urlPath}`, request, { headers: this.jsonOptions.headers, observe: 'response', responseType: 'blob', withCredentials: this.withCredentials })
      .pipe(catchError(this.handleBlobError(operation)));
  }

  protected postFile<TResponse>(operation: string, urlPath: string, formData: FormData): Observable<NgResponse<TResponse>> {
      return this.httpClient.post<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, formData, { withCredentials: this.withCredentials })
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected postFileWithBlobResponse(operation: string, urlPath: string, formData: FormData): Observable<HttpResponse<Blob>> {
      return this.httpClient.post(`${this.apiUrl}${urlPath}`, formData, { observe: 'response', responseType: 'blob', withCredentials: this.withCredentials })
      .pipe(catchError(this.handleBlobError(operation)));
  }

  protected patch<TResponse>(operation: string, urlPath: string): Observable<NgResponse<TResponse>> {
    return this.httpClient.patch<NgResponse<TResponse>>(urlPath, null, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected patchBody<TRequest, TResponse>(operation: string,
    urlPath: string,
    request: NgRequest<TRequest>): Observable<NgResponse<TResponse>> {
    return this.httpClient.patch<NgResponse<TResponse>>(urlPath, request, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected put<TResponse>(operation: string, urlPath: string): Observable<NgResponse<TResponse>> {
      return this.httpClient.put<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, null, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }

  protected putBody<TRequest, TResponse>(operation: string, urlPath: string, request: NgRequest<TRequest>): Observable<NgResponse<TResponse>> {
      return this.httpClient.put<NgResponse<TResponse>>(`${this.apiUrl}${urlPath}`, request, this.jsonOptions)
      .pipe(map(r => new NgResponse<TResponse>(r)), catchError(this.handleError<TResponse>(operation)));
  }
}